import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { serviceAPI, categoryAPI } from '../utils/api';
import './CreateService.css';

export default function EditService() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [service, setService] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', price: '', priceType: 'fixed',
    categoryId: '', tags: '', deliveryTime: '', city: '', state: '', country: 'US', isActive: true,
  });

  useEffect(() => {
    Promise.all([serviceAPI.getById(id), categoryAPI.getAll()]).then(([svcRes, catRes]) => {
      const s = svcRes.data;
      setService(s);
      setCategories(catRes.data);
      setForm({
        title: s.title || '',
        description: s.description || '',
        price: s.price?.toString() || '',
        priceType: s.priceType || 'fixed',
        categoryId: s.categoryId || '',
        tags: s.tags?.join(', ') || '',
        deliveryTime: s.deliveryTime || '',
        city: s.city || '',
        state: s.state || '',
        country: s.country || 'US',
        isActive: s.isActive,
      });
    }).catch(() => navigate('/dashboard/seller'));
  }, [id, navigate]);

  const handleChange = e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [e.target.name]: val }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        price: parseFloat(form.price),
        tags: form.tags ? form.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean) : [],
      };
      await serviceAPI.update(id, data);
      toast.success('Service updated!');
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      const { data } = await serviceAPI.uploadImages(id, fd);
      setService(s => ({ ...s, images: data.images }));
      toast.success('Images uploaded!');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!service) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div className="create-service-page page">
      <div className="container">
        <div className="create-header">
          <div className="flex items-center gap-2 mb-1">
            <Link to="/dashboard/seller" className="text-light text-sm">← Back to Dashboard</Link>
          </div>
          <h1>Edit Service</h1>
          <Link to={`/services/${id}`} className="text-primary text-sm">View live listing →</Link>
        </div>

        {/* Images */}
        <div className="create-section" style={{ marginBottom: 24 }}>
          <h2>📸 Service Photos</h2>
          <div className="images-grid">
            {service.images?.map((img, i) => (
              <div key={i} className="image-preview">
                <img src={`http://localhost:5000${img}`} alt={`Service ${i+1}`} />
              </div>
            ))}
            <label className="image-upload-btn">
              {uploading ? '⏳' : '+ Add Photo'}
              <input type="file" multiple accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            </label>
          </div>
          <span className="form-hint">Upload up to 5 photos. JPG, PNG, WebP, max 5MB each.</span>
        </div>

        <form onSubmit={handleSubmit} className="create-form">
          <div className="create-section">
            <h2>📋 Basic Information</h2>
            <div className="form-group">
              <label className="form-label">Service Title *</label>
              <input type="text" name="title" className="form-input" value={form.title} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea name="description" className="form-textarea" style={{ minHeight: 160 }} value={form.description} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select name="categoryId" className="form-select" value={form.categoryId} onChange={handleChange} required>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                <span className="form-label" style={{ margin: 0 }}>Service is active (visible to customers)</span>
              </label>
            </div>
          </div>

          <div className="create-section">
            <h2>💰 Pricing</h2>
            <div className="price-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Price (USD) *</label>
                <input type="number" name="price" className="form-input" value={form.price} onChange={handleChange} min="0" step="0.01" required />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Price Type</label>
                <select name="priceType" className="form-select" value={form.priceType} onChange={handleChange}>
                  <option value="fixed">Fixed Price</option>
                  <option value="hourly">Per Hour</option>
                  <option value="starting_at">Starting At</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Delivery / Turnaround Time</label>
              <input type="text" name="deliveryTime" className="form-input" value={form.deliveryTime} onChange={handleChange} />
            </div>
          </div>

          <div className="create-section">
            <h2>📍 Location</h2>
            <div className="location-row">
              <div className="form-group"><label className="form-label">City</label><input type="text" name="city" className="form-input" value={form.city} onChange={handleChange} /></div>
              <div className="form-group"><label className="form-label">State</label><input type="text" name="state" className="form-input" value={form.state} onChange={handleChange} /></div>
              <div className="form-group"><label className="form-label">Country</label><input type="text" name="country" className="form-input" value={form.country} onChange={handleChange} /></div>
            </div>
          </div>

          <div className="create-section">
            <h2>🏷️ Tags</h2>
            <div className="form-group">
              <label className="form-label">Tags (comma-separated)</label>
              <input type="text" name="tags" className="form-input" value={form.tags} onChange={handleChange} placeholder="e.g. catering, west african, party food" />
            </div>
          </div>

          <div className="create-actions">
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard/seller')}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
              {loading ? '⏳ Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
